import { Injectable } from '@angular/core';
import { ContentType } from '../../models/enums/content-type.enum';
import { LessonStatus } from '../../models/enums/lesson-status.enum';
import type { Course } from '../../models/interfaces/course.interface';
import type { Class } from '../../models/interfaces/module-class.interface';
import type { Module } from '../../models/interfaces/module.interface';
import type { CourseContentSidebarLessonItem, CourseContentSidebarModuleItem } from '../../models/interfaces/sidebar.interface';
import { environment } from '../../../../environments/environment';
import { CourseService } from '../course/course.service';
import { ModuleService } from '../module/module.service';
import { UserCourseService } from '../user-course/user-course.service';
import { UserModuleClassProgressService } from '../user-module-class-progress/user-module-class-progress.service';
import { Api } from '../api/api';

const DEFAULT_MODULE_TITLE = 'Conteúdo do curso';

export interface SidebarState {
  modules: CourseContentSidebarModuleItem[];
  lessons: CourseContentSidebarLessonItem[];
  selectedModuleId: string;
  selectedModuleProgress: number;
  moduleTitle: string;
}

export interface CourseContentState {
  course: Course;
  userCourseProgress: number;
  progressByClassId: Map<string, number>;
  /** Ponto de retomada por aula: segundos do vídeo ou fração (0-1) do rich text. */
  positionByClassId: Map<string, number>;
  lastAccessedClassId: string | null;
  sidebarState: SidebarState;
}

interface LessonProgressResponse {
  classId: string;
  progress: number;
  position?: number | null;
  lastAccessedAt?: string | Date | null;
}

interface ClassProgressSnapshot {
  progressByClassId: Map<string, number>;
  positionByClassId: Map<string, number>;
  lastAccessedClassId: string | null;
  courseProgress: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class CourseContentService {

  constructor(
    private readonly courseService: CourseService,
    private readonly moduleService: ModuleService,
    private readonly userCourseService: UserCourseService,
    private readonly userModuleClassProgressService: UserModuleClassProgressService,
    private readonly api: Api,
  ) {}

  public async load(courseId: string, userId?: string): Promise<CourseContentState | null> {
    if (environment.useFakeBackend) {
      return this.loadMocked(courseId, userId);
    }

    return this.loadInternal(courseId, userId, false);
  }

  public async loadMocked(courseId: string, userId?: string): Promise<CourseContentState | null> {
    return this.loadInternal(courseId, userId, true);
  }

  public async updateClassProgress(
    userId: string,
    classId: string,
    course: Course,
    progressByClassId: Map<string, number>,
    progress: number,
    currentCourseProgress: number,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    if (environment.useFakeBackend) {
      return this.updateClassProgressMocked(userId, classId, course, progressByClassId, progress, currentCourseProgress);
    }

    return this.updateClassProgressInternal(userId, classId, course, progressByClassId, progress, currentCourseProgress, false);
  }

  public async updateClassProgressMocked(
    userId: string,
    classId: string,
    course: Course,
    progressByClassId: Map<string, number>,
    progress: number,
    currentCourseProgress: number,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    return this.updateClassProgressInternal(userId, classId, course, progressByClassId, progress, currentCourseProgress, true);
  }

  public async updateClassPosition(
    userId: string,
    classId: string,
    course: Course,
    progress: number,
    position: number,
  ): Promise<void> {
    if (environment.useFakeBackend) {
      return this.updateClassPositionMocked(userId, classId, course, progress, position);
    }

    return this.updateClassPositionInternal(userId, classId, course, progress, position, false);
  }

  public async updateClassPositionMocked(
    userId: string,
    classId: string,
    course: Course,
    progress: number,
    position: number,
  ): Promise<void> {
    return this.updateClassPositionInternal(userId, classId, course, progress, position, true);
  }

  public async markClassComplete(
    userId: string,
    classId: string,
    course: Course,
    progressByClassId: Map<string, number>,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    if (environment.useFakeBackend) {
      return this.markClassCompleteMocked(userId, classId, course, progressByClassId);
    }

    return this.markClassCompleteInternal(userId, classId, course, progressByClassId, false);
  }

  public async markClassCompleteMocked(
    userId: string,
    classId: string,
    course: Course,
    progressByClassId: Map<string, number>,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    return this.markClassCompleteInternal(userId, classId, course, progressByClassId, true);
  }

  public async resetModuleProgress(
    userId: string,
    moduleId: string,
    course: Course,
    progressByClassId: Map<string, number>,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    if (environment.useFakeBackend) {
      return this.resetModuleProgressMocked(userId, moduleId, course, progressByClassId);
    }

    return this.resetModuleProgressInternal(userId, moduleId, course, progressByClassId, false);
  }

  public async resetModuleProgressMocked(
    userId: string,
    moduleId: string,
    course: Course,
    progressByClassId: Map<string, number>,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    return this.resetModuleProgressInternal(userId, moduleId, course, progressByClassId, true);
  }

  public getSidebarState(course: Course, progressByClassId: Map<string, number>, selectedModuleId: string = ''): SidebarState {
    const orderedModules = this.getOrderedModules(course).map((module) => ({
      ...module,
      classes: [...(module.classes ?? [])].sort((first, second) => first.order - second.order),
    }));
    const modules = this.mapModulesWithCompletion(orderedModules, progressByClassId);

    if (!orderedModules.length) {
      return {
        modules,
        lessons: [],
        selectedModuleId: '',
        selectedModuleProgress: 0,
        moduleTitle: course.title || DEFAULT_MODULE_TITLE,
      };
    }

    const selectedModule = this.resolveSelectedModule(orderedModules, selectedModuleId);
    const orderedClasses = selectedModule.classes ?? [];
    const selectedModuleProgress = this.calculateAverageProgress(orderedClasses, progressByClassId);

    return {
      modules,
      lessons: this.mapClassesToLessons(orderedClasses, progressByClassId),
      selectedModuleId: selectedModule.id || '',
      selectedModuleProgress,
      moduleTitle: selectedModule.title || course.title || DEFAULT_MODULE_TITLE,
    };
  }

  private async loadInternal(courseId: string, userId: string | undefined, useMocked: boolean): Promise<CourseContentState | null> {
    const course = useMocked
      ? await this.courseService.getByIdMocked(courseId)
      : await this.courseService.getById(courseId);

    if (!course) {
      return null;
    }

    const modules = await this.resolveModules(course, useMocked);
    const moduleIds = modules.map((module) => module.id);
    const classes = await this.resolveClasses(moduleIds, useMocked);
    const classesByModuleId = this.groupClassesByModuleId(classes);

    const modulesWithClasses = modules.map((module) => ({
      ...module,
      classes: classesByModuleId[module.id] ?? [],
    }));

    const resolvedUserId = userId ?? '';
    const shouldUseClassProgress = this.shouldUseClassProgress(classes, course.id);
    const lessonProgress: ClassProgressSnapshot = shouldUseClassProgress
      ? await this.getClassProgressByClassId(resolvedUserId, classes, course.id, useMocked)
      : this.emptyClassProgressSnapshot();
    const classProgressByClassId = lessonProgress.progressByClassId;
    const userCourseProgress = lessonProgress.courseProgress
      ?? await this.getUserCourseProgress(resolvedUserId, course.id, useMocked);
    const progressByClassId = classProgressByClassId.size
      ? classProgressByClassId
      : this.buildProgressByClassId(classes, userCourseProgress);
    const resolvedUserCourseProgress = this.resolveUserCourseProgress(userCourseProgress, classes, progressByClassId);

    const hydratedCourse: Course = {
      ...course,
      modules: modulesWithClasses,
    };

    return {
      course: hydratedCourse,
      userCourseProgress: resolvedUserCourseProgress,
      progressByClassId,
      positionByClassId: lessonProgress.positionByClassId,
      lastAccessedClassId: lessonProgress.lastAccessedClassId,
      sidebarState: this.getSidebarState(hydratedCourse, progressByClassId),
    };
  }

  private async resolveModules(course: Course, useMocked: boolean): Promise<Module[]> {
    if (course.modulesIds?.length) {
      return useMocked
        ? this.moduleService.getByIdsMocked(course.modulesIds)
        : this.moduleService.getByIds(course.modulesIds);
    }

    return useMocked
      ? this.moduleService.getByCourseIdMocked(course.id)
      : this.moduleService.getByCourseId(course.id);
  }

  private async resolveClasses(moduleIds: string[], useMocked: boolean): Promise<Class[]> {
    if (!moduleIds.length) {
      return [];
    }

    return useMocked
      ? this.moduleService.getClassesByModuleIdsMocked(moduleIds)
      : this.moduleService.getClassesByModuleIds(moduleIds);
  }

  private async updateClassProgressInternal(
    userId: string,
    classId: string,
    course: Course,
    progressByClassId: Map<string, number>,
    progress: number,
    currentCourseProgress: number,
    useMocked: boolean,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    const normalized = this.normalizeProgress(progress);
    const current = this.normalizeProgress(progressByClassId.get(classId) ?? 0);

    if (normalized <= current) {
      return {
        progressByClassId: new Map(progressByClassId),
        userCourseProgress: currentCourseProgress,
      };
    }

    if (useMocked) {
      await this.userModuleClassProgressService.updateProgressMocked(userId, classId, normalized);
      const updatedMap = new Map(progressByClassId);
      updatedMap.set(classId, normalized);
      return this.persistCourseProgressFromClassMap(userId, course, updatedMap, true);
    }

    const contentId = this.resolveContentId(course, classId);
    const updated = await this.api.patchAsync<{ classId: string; progress: number }>(`/contents/${contentId}/progress`, {
      progress: normalized,
    });

    if (updated.error) {
      throw updated.error;
    }

    const savedProgress = this.normalizeProgress(updated.success?.progress ?? normalized);
    const updatedMap = new Map(progressByClassId);
    updatedMap.set(classId, Math.max(savedProgress, normalized));

    if (savedProgress < 100) {
      return {
        progressByClassId: updatedMap,
        userCourseProgress: currentCourseProgress,
      };
    }

    const listed = await this.fetchCourseLessonProgress(course.id);

    listed.lessons.forEach((lesson) => {
      if (lesson.classId) {
        updatedMap.set(lesson.classId, this.normalizeProgress(lesson.progress));
      }
    });

    return {
      progressByClassId: updatedMap,
      userCourseProgress: listed.courseProgress,
    };
  }

  private async updateClassPositionInternal(
    userId: string,
    classId: string,
    course: Course,
    progress: number,
    position: number,
    useMocked: boolean,
  ): Promise<void> {
    if (!Number.isFinite(position) || position < 0) return;

    if (useMocked) {
      await this.userModuleClassProgressService.updatePositionMocked(userId, classId, position);
      return;
    }

    const contentId = this.resolveContentId(course, classId);
    const updated = await this.api.patchAsync<LessonProgressResponse>(`/contents/${contentId}/progress`, {
      progress: this.normalizeProgress(progress),
      position,
    });

    if (updated.error) {
      throw updated.error;
    }
  }

  private async markClassCompleteInternal(
    userId: string,
    classId: string,
    course: Course,
    progressByClassId: Map<string, number>,
    useMocked: boolean,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    if (useMocked) {
      await this.userModuleClassProgressService.updateProgressMocked(userId, classId, 100);
      const updatedMap = new Map(progressByClassId);
      updatedMap.set(classId, 100);
      return this.persistCourseProgressFromClassMap(userId, course, updatedMap, true);
    }

    const contentId = this.resolveContentId(course, classId);
    const completed = await this.api.postAsync<{ classId: string; progress: number }>(`/contents/${contentId}/complete`, {});

    if (completed.error) {
      throw completed.error;
    }

    const listed = await this.fetchCourseLessonProgress(course.id);
    const updatedMap = new Map(progressByClassId);

    if (listed.lessons.length) {
      listed.lessons.forEach((lesson) => {
        if (lesson.classId) {
          updatedMap.set(lesson.classId, this.normalizeProgress(lesson.progress));
        }
      });
    } else {
      updatedMap.set(classId, 100);
    }

    return {
      progressByClassId: updatedMap,
      userCourseProgress: listed.courseProgress,
    };
  }

  private async resetModuleProgressInternal(
    userId: string,
    moduleId: string,
    course: Course,
    progressByClassId: Map<string, number>,
    useMocked: boolean,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    const targetModule = course.modules?.find((module) => module.id === moduleId);
    const moduleClasses = targetModule?.classes ?? [];

    if (!moduleClasses.length) {
      return {
        progressByClassId: new Map(progressByClassId),
        userCourseProgress: Math.round(this.calculateAverageProgress(course.modules?.flatMap((module) => module.classes ?? []) ?? [], progressByClassId)),
      };
    }

    if (useMocked) {
      await Promise.all(
        moduleClasses.map((moduleClass) => this.userModuleClassProgressService.updateProgressMocked(userId, moduleClass.id, 0)),
      );
    } else {
      await Promise.all(
        moduleClasses.map((moduleClass) => this.userModuleClassProgressService.updateProgress(userId, moduleClass.id, 0)),
      );
    }

    const updatedMap = new Map(progressByClassId);
    moduleClasses.forEach((moduleClass) => {
      updatedMap.set(moduleClass.id, 0);
    });

    return this.persistCourseProgressFromClassMap(userId, course, updatedMap, useMocked);
  }

  private async persistCourseProgressFromClassMap(
    userId: string,
    course: Course,
    updatedMap: Map<string, number>,
    useMocked: boolean,
  ): Promise<{ progressByClassId: Map<string, number>; userCourseProgress: number }> {
    const allClasses = course.modules?.flatMap((module) => module.classes ?? []) ?? [];
    const newCourseProgress = Math.round(this.calculateAverageProgress(allClasses, updatedMap));

    const userCourse = useMocked
      ? await this.userCourseService.getUserCourseMocked(userId, course.id)
      : await this.userCourseService.getUserCourse(userId, course.id);

    if (userCourse) {
      if (useMocked) {
        await this.userCourseService.updateProgressMocked(course.id, newCourseProgress);
      } else {
        await this.userCourseService.updateProgress(course.id, newCourseProgress);
      }
    }

    return { progressByClassId: updatedMap, userCourseProgress: newCourseProgress };
  }

  private getOrderedModules(course: Course): Module[] {
    return [...(course.modules ?? [])].sort((first, second) => first.order - second.order);
  }

  private resolveSelectedModule(orderedModules: Module[], selectedModuleId: string): Module {
    return orderedModules.find((module) => module.id === selectedModuleId) ?? orderedModules[0];
  }

  private mapModulesWithCompletion(modules: Module[], progressByClassId: Map<string, number>): CourseContentSidebarModuleItem[] {
    return modules
      .map((module) => {
        const moduleClasses = module.classes ?? [];
        const isCompleted = moduleClasses.length > 0 && moduleClasses.every((moduleClass) => this.getClassProgress(moduleClass.id, progressByClassId) >= 100);

        return {
          id: module.id || '',
          title: module.title,
          order: module.order,
          isCompleted,
        };
      })
      .filter((module) => !!module.id);
  }

  private calculateAverageProgress(classes: Class[], progressByClassId: Map<string, number>): number {
    if (!classes.length) return 0;

    const totalProgress = classes.reduce((sum, moduleClass) => sum + this.getClassProgress(moduleClass.id, progressByClassId), 0);

    return totalProgress / classes.length;
  }

  private mapClassesToLessons(classes: Class[], progressByClassId: Map<string, number>): CourseContentSidebarLessonItem[] {
    if (!classes.length) return [];

    return classes
      .filter((moduleClass) => moduleClass.type !== ContentType.ACTIVITY)
      .map((lesson, index) => {
      const lessonProgress = this.getClassProgress(lesson.id, progressByClassId);

      return {
        id: lesson.id || `${lesson.title}-${index}`,
        title: lesson.title,
        duration: lesson.duration,
        status: this.getLessonStatus(lessonProgress),
        lessonProgress,
      };
      });
  }

  private getLessonStatus(progress: number): LessonStatus {
    if (progress >= 100) return LessonStatus.COMPLETED;
    if (progress > 0) return LessonStatus.IN_PROGRESS;
    return LessonStatus.NOT_STARTED;
  }

  private groupClassesByModuleId(classes: Class[]): Record<string, Class[]> {
    const grouped = classes.reduce<Record<string, Class[]>>((accumulator, moduleClass) => {
      if (!accumulator[moduleClass.moduleId]) {
        accumulator[moduleClass.moduleId] = [];
      }

      accumulator[moduleClass.moduleId].push(moduleClass);

      return accumulator;
    }, {});

    Object.values(grouped).forEach((moduleClasses) => {
      moduleClasses.sort((first, second) => first.order - second.order);
    });

    return grouped;
  }

  private getClassProgress(classId: string, progressByClassId: Map<string, number>): number {
    return this.normalizeProgress(progressByClassId.get(classId) ?? 0);
  }

  private buildProgressByClassId(classes: Class[], userCourseProgress: number): Map<string, number> {
    const progressByClassId = new Map<string, number>();

    if (!classes.length) {
      return progressByClassId;
    }

    const normalizedCourseProgress = this.normalizeProgress(userCourseProgress);
    const completedLessonUnits = (normalizedCourseProgress / 100) * classes.length;

    classes.forEach((moduleClass, index) => {
      const remainingUnits = completedLessonUnits - index;
      let lessonProgress = 0;

      if (remainingUnits >= 1) {
        lessonProgress = 100;
      } else if (remainingUnits > 0) {
        lessonProgress = remainingUnits * 100;
      }

      progressByClassId.set(moduleClass.id, this.normalizeProgress(lessonProgress));
    });

    return progressByClassId;
  }

  private async getUserCourseProgress(userId: string, courseId: string, useMocked = false): Promise<number> {
    const userCourse = useMocked
      ? await this.userCourseService.getUserCourseMocked(userId, courseId)
      : await this.userCourseService.getUserCourse(userId, courseId);

    return this.normalizeProgress(userCourse?.completedAt ? 100 : userCourse?.progress ?? 0);
  }

  private async getClassProgressByClassId(
    userId: string,
    classes: Class[],
    courseId: string,
    useMocked = false,
  ): Promise<ClassProgressSnapshot> {
    const classIds = classes.map((moduleClass) => moduleClass.id);

    if (!classIds.length) {
      return this.emptyClassProgressSnapshot();
    }

    if (useMocked) {
      const classProgresses = await this.userModuleClassProgressService.getByUserAndClassIdsMocked(userId, classIds);

      return { ...this.toClassProgressSnapshot(classProgresses), courseProgress: null };
    }

    const listed = await this.fetchCourseLessonProgress(courseId);

    return {
      ...this.toClassProgressSnapshot(listed.lessons),
      courseProgress: listed.lessons.length ? listed.courseProgress : null,
    };
  }

  private toClassProgressSnapshot(lessons: LessonProgressResponse[]): Omit<ClassProgressSnapshot, 'courseProgress'> {
    const progressByClassId = new Map<string, number>();
    const positionByClassId = new Map<string, number>();
    let lastAccessedClassId: string | null = null;
    let lastAccessedTime = Number.NEGATIVE_INFINITY;

    lessons.forEach((lesson) => {
      if (!lesson.classId) return;

      progressByClassId.set(lesson.classId, this.normalizeProgress(lesson.progress));

      if (typeof lesson.position === 'number' && Number.isFinite(lesson.position) && lesson.position >= 0) {
        positionByClassId.set(lesson.classId, lesson.position);
      }

      const accessedTime = lesson.lastAccessedAt ? new Date(lesson.lastAccessedAt).getTime() : Number.NaN;
      if (accessedTime > lastAccessedTime) {
        lastAccessedTime = accessedTime;
        lastAccessedClassId = lesson.classId;
      }
    });

    return { progressByClassId, positionByClassId, lastAccessedClassId };
  }

  private emptyClassProgressSnapshot(): ClassProgressSnapshot {
    return {
      progressByClassId: new Map<string, number>(),
      positionByClassId: new Map<string, number>(),
      lastAccessedClassId: null,
      courseProgress: null,
    };
  }

  private async fetchCourseLessonProgress(courseId: string): Promise<{ courseProgress: number; lessons: LessonProgressResponse[] }> {
    const response = await this.api.getAsync<{ courseProgress: number; lessons: LessonProgressResponse[] }>(
      `/contents/course/${courseId}/progress`,
    );

    if (response.error) {
      throw response.error;
    }

    return {
      courseProgress: this.normalizeProgress(response.success?.courseProgress ?? 0),
      lessons: response.success?.lessons ?? [],
    };
  }

  private resolveContentId(course: Course, classId: string): string {
    const moduleClass = course.modules?.flatMap((module) => module.classes ?? []).find((item) => item.id === classId);

    return moduleClass?.contentId ?? classId;
  }

  private shouldUseClassProgress(classes: Class[], courseId: string): boolean {
    return (classes as Array<Class & { courseId?: string }>).every((moduleClass) => !moduleClass.courseId || moduleClass.courseId === courseId);
  }

  private resolveUserCourseProgress(userCourseProgress: number, classes: Class[], progressByClassId: Map<string, number>): number {
    const normalizedUserCourseProgress = this.normalizeProgress(userCourseProgress);

    if (normalizedUserCourseProgress > 0 || !classes.length) {
      return normalizedUserCourseProgress;
    }

    return this.calculateAverageProgress(classes, progressByClassId);
  }

  private normalizeProgress(progress: number): number {
    return Math.min(100, Math.max(0, progress || 0));
  }
}